<template>
  <div class="schedule-details">
    <div class="schedule schedule-details__content">
      <div class="info">
        <div class="header" v-if="schedule.variations?.length && schedule.seatsCount === 0">
          <div class="title">
            <span class="fa fa-chair schedule-icon"></span>
            {{ schedule.name }}
          </div>
        </div>
        <div class="variations pl-4"
             v-if="schedule.variations?.length && schedule.seatsCount === 0"
        >
          <div class="variation-info" v-for="variation in sortedVariations" :key="variation.id">
            <div class="variation-details">
              <span class="variation-name" v-if="schedule.variations.length > 1">
                {{ variation.name }}:
              </span>
              <span class="variation-schedule">
                {{ variation.daysOfWeek }}, {{ variation.timeRange }}
              </span>
            </div>
            <div class="variation-price">
              {{ formatPrice(variation.costAmount, variation.costFrequency) }}
            </div>
          </div>
        </div>
        <div v-else-if="schedule.seatsCount > 0" class="seat-details">
          <expandable-spot-list
            :spots="sortedSeats(schedule.seats)">
          </expandable-spot-list>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import expandableSpotList from '@profile/components/expandable-spot-list.vue';

const props = defineProps({
  schedule: {
    type: Object,
    required: true,
  },
});

const sortVariations = (variations) => {
  if (!variations) return [];

  return variations.sort((a, b) => {
    const d1 = Date.parse(`01 Jan 1970 ${a.startTime}`);
    const d2 = Date.parse(`01 Jan 1970 ${b.startTime}`);
    if (!d1 || !d2) return 0;
    return d1 < d2 ? -1 : 1;
  });
};

const sortedSeats = (seats) => {
  if (!seats) return [];

  return seats.sort((a, b) => {
    if (a.availableDate && b.availableDate) {
      const dateA = new Date(a.availableDate);
      const dateB = new Date(b.availableDate);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
    }
    return a.priceName.localeCompare(b.priceName);
  });
};

const formatPrice = (price, frequency) => {
  if (!price) return '';

  const formattedPrice = typeof price === 'number' && price >= 100
    ? (price / 100).toFixed(0)
    : price;

  return `$${formattedPrice} / ${frequency}`;
};

const sortedVariations = computed(() => sortVariations(props.schedule.variations));

</script>

<style scoped>
.schedule-icon {
  color: var(--gray-300);
  margin-right: calc(var(--grid-unit) * 1.5);
}

.schedule-details {
  margin-bottom: calc(var(--grid-unit) * 2);
}

.schedule-details__content {
  line-height: 1.6;
}

.schedule-options-label {
  font-weight: 500;
  color: var(--sapphire-500);
  margin-bottom: var(--grid-unit);
  font-size: 14px;
}

.schedule {
  border-radius: var(--grid-unit);
  border: 1px solid var(--gray-300);
  margin-bottom: calc(var(--grid-unit) * 2);
}

.info {
  padding: calc(var(--grid-unit) * 2);
  padding-right: 0;

  @media screen and (min-width: 768px) {
    padding: calc(var(--grid-unit) * 2) calc(var(--grid-unit) * 3);
  }
}

.header {
  display: grid;
  grid-template-columns: auto auto;
}

.title {
  font-size: 20px;
  font-weight: 400;
  padding-left: calc(var(--grid-unit) * 2);
}

.variations {
  font-size: 13px;

  @media screen and (min-width: 768px) {
    font-size: 18px;
  }
}

.variation-info {
  display: flex;
  justify-content: space-between;
  color: var(--gray-almostBlack);

  @media screen and (max-width: 768px) {
    gap: 8px;
    display: grid;
  }
}

.variation-details {
  gap: 2px;
}

.variation-name {
  color: var(--gray-almostBlack);
  font-size: 16px;
  font-weight: 500;
  padding-left: calc(var(--grid-unit) * 2);

  @media screen and (max-width: 768px) {
    display: block;
  }
}

.variation-schedule {
  padding-left: calc(var(--grid-unit) * 2);
  color: var(--gray-almostBlack);
  font-size: 16px;
}

.variation-price {
  color: var(--gray-almostBlack);
  white-space: nowrap;
  font-size: 16px;
}
</style>
